// Request/response schemas for the R10 messaging + tasks + notifications
// surface. docs/replan-v2.md R10.
//
// Messaging: teacher ↔ student 1:1 + teacher → class broadcast. A student's
// parent (if the student is also a family kid) sees the thread read-only.
// Tasks: teacher-issued non-lesson to-dos with a done → verified loop.
// Notifications: in-app inbox + per-(channel,kind) preferences.

import { z } from 'zod';

// ─── Messaging ───────────────────────────────────────────────────────────────

// Body cap mirrors the messages.body column (text, but we bound the API surface).
export const messageBodySchema = z.string().trim().min(1, 'Write a message').max(2000);

// Teacher starts / continues a 1:1 with one of their students.
export const sendDirectMessageInput = z.object({
  studentUserId: z.uuid(),
  body: messageBodySchema,
});
export type SendDirectMessageInput = z.infer<typeof sendDirectMessageInput>;

// A student replies inside an existing conversation they're a participant of.
export const replyMessageInput = z.object({
  body: messageBodySchema,
});
export type ReplyMessageInput = z.infer<typeof replyMessageInput>;

// Teacher broadcasts to every student in one of their classes (no replies).
export const classBroadcastInput = z.object({
  classId: z.uuid(),
  body: messageBodySchema,
});
export type ClassBroadcastInput = z.infer<typeof classBroadcastInput>;

export const conversationSummaryDto = z.object({
  id: z.uuid(),
  kind: z.enum(['1to1', 'class_broadcast']),
  // The "other side" label for the list row: the student's name in a teacher's
  // view, the teacher's name in a student's view, or the class name.
  title: z.string(),
  classId: z.uuid().nullable(),
  lastMessageAt: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  unreadCount: z.number(),
  // True when the caller is a read-only observer (parent watching their kid).
  observer: z.boolean(),
});
export type ConversationSummaryDto = z.infer<typeof conversationSummaryDto>;

export const messageDto = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  senderUserId: z.uuid(),
  senderName: z.string(),
  body: z.string(),
  mine: z.boolean(),
  // 'flagged'/'blocked' messages render as "held for review" to non-admins.
  moderationStatus: z.enum(['clean', 'flagged', 'approved', 'blocked']),
  createdAt: z.string(),
});
export type MessageDto = z.infer<typeof messageDto>;

// ─── Tasks ───────────────────────────────────────────────────────────────────

export const taskCreateInput = z.object({
  // Free-text, non-i18n title (teacher's own words). The schema stores it in
  // tasks.title_text; title_key is reserved for system-generated tasks.
  title: z.string().trim().min(1).max(240),
  // Assign to specific students, or to a whole class (expanded server-side).
  assigneeUserIds: z.array(z.uuid()).max(200).optional().default([]),
  classId: z.uuid().nullable().optional(),
  dueAt: z.iso.datetime().nullable().optional(),
});
export type TaskCreateInput = z.infer<typeof taskCreateInput>;

export const taskStatusSchema = z.enum(['open', 'in_progress', 'done', 'verified']);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

// A student marks their own task done; a teacher verifies (or reopens).
export const taskTransitionInput = z.object({
  status: taskStatusSchema,
});
export type TaskTransitionInput = z.infer<typeof taskTransitionInput>;

export const taskDto = z.object({
  id: z.uuid(),
  title: z.string(),
  status: taskStatusSchema,
  assigneeUserId: z.uuid(),
  assigneeName: z.string().nullable(),
  createdByUserId: z.uuid(),
  dueAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type TaskDto = z.infer<typeof taskDto>;

// ─── Notifications ─────────────────────────────────────────────────────────

export const notificationDto = z.object({
  id: z.uuid(),
  kind: z.string(),
  // Pre-rendered payload the bell dropdown reads (titleKey/bodyKey + params +
  // an optional deep link). Kept loose — the inbox renders generically.
  payload: z.record(z.string(), z.unknown()),
  readAt: z.string().nullable(),
  createdAt: z.string(),
});
export type NotificationDto = z.infer<typeof notificationDto>;

export const markReadInput = z.object({
  // Mark specific ids read, or all when omitted.
  ids: z.array(z.uuid()).max(500).optional(),
});
export type MarkReadInput = z.infer<typeof markReadInput>;

export const notificationChannelSchema = z.enum(['in_app', 'email', 'push', 'sms', 'whatsapp']);

export const preferenceUpdateInput = z.object({
  kind: z.string().min(1).max(80),
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});
export type PreferenceUpdateInput = z.infer<typeof preferenceUpdateInput>;

export const preferenceDto = z.object({
  kind: z.string(),
  channel: notificationChannelSchema,
  enabled: z.boolean(),
});
export type PreferenceDto = z.infer<typeof preferenceDto>;

// ─── Web Push (R11) ──────────────────────────────────────────────────────────

// The browser's PushSubscription, flattened to what the server stores.
export const pushSubscribeInput = z.object({
  endpoint: z.url(),
  keys: z.object({
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(100),
  }),
});
export type PushSubscribeInput = z.infer<typeof pushSubscribeInput>;

export const pushUnsubscribeInput = z.object({
  endpoint: z.url(),
});
export type PushUnsubscribeInput = z.infer<typeof pushUnsubscribeInput>;
